import React, { Fragment, useCallback, useMemo } from "react";
import { createEditor } from "slate";
import { Slate, Editable, withReact } from "slate-react";
import { StageElement, type ViewerDeviceRuntime } from "../../../types";
import type { Media } from "@bronte/viewer";
import { RichTextEditor } from "@pearsontechnology/aero-components-v2";

// local plugins from ./Plugins
import InlineIcon from "./Plugins/InlineIcon";
import Link from "./Plugins/Link/Plugin";
import InlineText from "./Plugins/InlineText/Plugin";
import ReferenceLink from "./Plugins/ReferenceLink/Plugin";
import MathMLExpressionDefault from "./Plugins/MathMLExpressionDefault";
import CollapsibleText from "./Plugins/CollapsibleText";
import KeyTerm from "./Plugins/KeyTerm";
import Footnote from "./Plugins/Footnote";
import PopupTip from "./Plugins/PopupTip";
import SpotlightBlockIds from "./Plugins/SpotlightBlockIds/Plugin";
import LegacyInline from "./Plugins/LegacyInline/Plugin";
import { normalizeInitialValue } from "../../../util/normalizeSlateValue";
import type { InlineIconConfig } from "./Plugins/InlineIcon/DefaultConfig";

interface TextElementProps {
  element: StageElement & { value?: [] };
  onClickReferenceLink?: (dataId: string, referenceId: string) => void;
  onMediaClick?: (data: Media) => void;
  viewerDevice?: ViewerDeviceRuntime;
}

const TextElement: React.FC<TextElementProps> = ({
  element,
  onClickReferenceLink,
  onMediaClick,
  viewerDevice,
}) => {
  const { value } = element;
  const config = (element.config ?? {}) as {
    cssClass?: string;
    alignment?: string;
    pluginConfigs?: Record<string, InlineIconConfig>;
  };
  const pluginConfigs = config.pluginConfigs;
  const plugins = useMemo(() => {
    return [
      SpotlightBlockIds(),
      RichTextEditor.Plugins.Paragraph.Plugin(),
      RichTextEditor.Plugins.BoldItalicUnderline.Plugin(),
      RichTextEditor.Plugins.CustomParagraph.Plugin(),
      RichTextEditor.Plugins.LineHeight.Plugin(),
      RichTextEditor.Plugins.AutoInsertLists.Plugin(),
      RichTextEditor.Plugins.Headings.Plugin(),
      RichTextEditor.Plugins.SuperSubScript.Plugin(),
      RichTextEditor.Plugins.Highlight.Plugin(),
      RichTextEditor.Plugins.TextColor.Plugin(),
      RichTextEditor.Plugins.StyledText.Plugin(true),
      // local plugins from ./Plugins
      InlineIcon.Plugin({ pluginConfigs, viewerDevice }),
      Link(),
      InlineText(),
      ReferenceLink(onClickReferenceLink),
      MathMLExpressionDefault.Plugin(),
      CollapsibleText.Plugin(),
      KeyTerm.Plugin(),
      Footnote.Plugin(),
      PopupTip.Plugin(onMediaClick, viewerDevice),
      LegacyInline(),
    ];
  }, [pluginConfigs, onClickReferenceLink, onMediaClick, viewerDevice]);

  const editor = useMemo(() => {
    let baseEditor = withReact(createEditor());
    plugins.forEach((plugin) => {
      baseEditor = plugin(baseEditor);
      return baseEditor;
    });
    return baseEditor;
  }, [plugins]);

  const normalizedValue = useMemo(
    () => normalizeInitialValue(editor, value || []),
    [editor, value],
  );

  const renderElement = useCallback(
    (props: any) => {
      for (const plugin of plugins) {
        if (plugin.renderElement) {
          const result = plugin.renderElement(props);
          if (result) return result;
        }
      }
      // Default rendering for any unhandled element types
      const { element, attributes, children } = props;
      const data = element?.data || {};
      const blockId = data.id || data.dataId || element?.id;
      if (blockId) {
        return (
          <div {...attributes} id={blockId} data-id={blockId}>
            {children}
          </div>
        );
      }
      return <div {...attributes}>{children}</div>;
    },
    [plugins],
  );

  const renderLeaf = useCallback(
    (props: any) => {
      let { children } = props;

      if (props.leaf.bold) children = <strong>{children}</strong>;
      if (props.leaf.italic) children = <em>{children}</em>;
      if (props.leaf.underline) children = <u>{children}</u>;
      if (props.leaf.code) children = <code>{children}</code>;
      if (props.leaf.strikethrough) children = <s>{children}</s>;

      for (const plugin of plugins) {
        if (plugin.renderLeaf) {
          const result = plugin.renderLeaf({ ...props, children });
          if (result !== undefined) children = result;
        }
      }

      return <span {...props.attributes}>{children}</span>;
    },
    [plugins],
  );

  return (
    <Fragment>
      <Slate editor={editor} initialValue={normalizedValue}>
        <Editable
          readOnly={true}
          className={config.cssClass}
          style={
            {
              overflowWrap: "normal",
              ...(config.alignment ? { textAlign: config.alignment } : {}),
            } as React.CSSProperties
          }
          renderElement={renderElement}
          renderLeaf={renderLeaf}
        />
      </Slate>
    </Fragment>
  );
};

export default TextElement;
